import { useCallback, useRef, useState } from 'react';

// Use sessionStorage instead of URL params to avoid re-renders on every URL change
const getStorageKey = (tableId: string): string => `table-scroll-${tableId}`;

const getScrollFromStorage = (tableId: string): number => {
  try {
    const value = sessionStorage.getItem(getStorageKey(tableId));

    return value ? Number(value) || 0 : 0;
  } catch {
    return 0;
  }
};

const setScrollInStorage = (tableId: string, scroll: number): void => {
  try {
    sessionStorage.setItem(getStorageKey(tableId), String(scroll));
  } catch {
    // Ignore storage errors
  }
};

export const useTableScroll = (tableId: string): [number, (tableScroll: number) => void] => {
  // Use ref to store scroll position without causing re-renders
  const scrollRef = useRef<number>(getScrollFromStorage(tableId));
  // Only use state for the initial value to be read on first render
  const [initialScroll] = useState(() => scrollRef.current);

  const setTableScroll = useCallback(
    (newScroll: number) => {
      scrollRef.current = newScroll;
      setScrollInStorage(tableId, newScroll);
    },
    [tableId]
  );

  // Return the ref's current value for reads (won't cause re-renders when changed)
  // The initial scroll is used for the first render only
  return [scrollRef.current || initialScroll, setTableScroll];
};
